import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Search,
  X,
  ArrowLeft,
  ArrowRight,
  MessageCircle,
  Clock,
  TrendingUp,
  Target,
  Briefcase,
  MapPin,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileImage,
  Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { CASE_STUDIES } from "../demo/DemoScreens";
import { monthlyResults } from "../data/mock";
import "./LandingPage.css";
import "./CaseStudiesPage.css";

/* ---------- Helpers ---------- */
const WHATSAPP_CTA_URL =
  "https://api.whatsapp.com/send?phone=917996325666&text=Hey%20Shubham,%20would%20like%20to%20take%20bulk%20job%20application%20services%20from%20you!";

const slugify = (s = "") =>
  s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const ALL = "All";

/* ---------- Compact Header ---------- */
function CaseHeader() {
  return (
    <header className="cs-page-header" data-testid="cs-page-header">
      <div className="cs-page-header-inner">
        <a href="/" className="cs-page-logo" data-testid="cs-page-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_jobblast/artifacts/ixjqwc10_image.png"
            alt="BulkJobApply"
            width="130"
            height="36"
          />
        </a>
        <nav className="cs-page-nav">
          <a href="/" className="cs-page-link">Home</a>
          <a href="/#how-it-works" className="cs-page-link">How It Works</a>
          <a href="/#pricing" className="cs-page-link">Pricing</a>
          <a href="/#faq" className="cs-page-link">FAQ</a>
        </nav>
        <a
          href={WHATSAPP_CTA_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => window.gtag_report_conversion && window.gtag_report_conversion()}
        >
          <Button className="cs-page-cta" data-testid="cs-header-cta">
            Get Started <ArrowRight className="w-4 h-4" />
          </Button>
        </a>
      </div>
    </header>
  );
}

/* ---------- Hero ---------- */
function CaseHero() {
  return (
    <section className="cs-hero" data-testid="cs-hero">
      <div className="cs-hero-inner">
        <span className="cs-hero-eyebrow">FEATURED CLIENT STORIES</span>
        <h1 className="cs-hero-title" data-testid="cs-hero-title">
          Real people. Real callbacks.
          <br />
          <span className="cs-hero-accent">Real offers.</span>
        </h1>
        <p className="cs-hero-sub">
          A handful of our top client wins — every screenshot, every WhatsApp
          conversation, every offer. Picked because their stories show what's possible.
        </p>
      </div>
    </section>
  );
}

/* ---------- Search & Filters ---------- */
function CaseFilters({
  query,
  setQuery,
  tag,
  setTag,
  tags,
}) {
  return (
    <section className="cs-filters" data-testid="cs-filters">
      <div className="cs-filters-inner">
        <div className="cs-search-wrap">
          <Search className="cs-search-icon w-5 h-5" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, role, pain point, or theme…"
            className="cs-search-input"
            data-testid="cs-search-input"
            aria-label="Search case studies"
          />
          {query && (
            <button
              className="cs-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              data-testid="cs-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="cs-filter-row" data-testid="cs-filter-tags">
          <div className="cs-pill-row cs-pill-row-scroll">
            {[ALL, ...tags].map((t) => (
              <button
                key={t}
                className={`cs-pill cs-pill-tag ${tag === t ? "cs-pill-active" : ""}`}
                onClick={() => setTag(t)}
                data-testid={`cs-pill-tag-${slugify(t)}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Card ---------- */
function CaseCard({ cs, onOpen }) {
  return (
    <button
      className="cs-card"
      onClick={() => onOpen(cs)}
      data-testid={`cs-card-${slugify(cs.name)}`}
      aria-label={`Open story for ${cs.name}`}
    >
      <div className="cs-card-media">
        <img
          src={cs.screenshotUrl}
          alt={`${cs.name}'s WhatsApp conversation preview`}
          loading="lazy"
        />
        <div className="cs-card-tag">{cs.tag}</div>
      </div>
      <div className="cs-card-body">
        <div className="cs-card-name-row">
          <h3 className="cs-card-name">{cs.name}</h3>
          <span className="cs-card-interviews">
            <MessageCircle className="w-3.5 h-3.5" />
            {cs.interviews} interviews
          </span>
        </div>
        <div className="cs-card-meta">
          <span className="cs-card-meta-item">
            <Briefcase className="w-3.5 h-3.5" />
            {cs.role}
          </span>
          <span className="cs-card-meta-dot">·</span>
          <span className="cs-card-meta-item">
            <MapPin className="w-3.5 h-3.5" />
            United States
          </span>
        </div>
        <p className="cs-card-pain">"{cs.painPoint}"</p>
        <div className="cs-card-result">
          <TrendingUp className="w-4 h-4" />
          <span>{cs.result}</span>
        </div>
        <div className="cs-card-foot">
          <Clock className="w-3.5 h-3.5" />
          <span>{cs.duration}</span>
          <span className="cs-card-foot-spacer" />
          <span className="cs-card-foot-cta">
            Read story <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </button>
  );
}

/* ---------- Detail / Modal ---------- */
function CaseDetail({ cs, onClose }) {
  const overlayRef = useRef(null);
  const [copyStatus, setCopyStatus] = useState("idle");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const shareUrl = `${window.location.origin}/casestudies/${slugify(cs.name)}`;
  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyStatus("ok");
      setTimeout(() => setCopyStatus("idle"), 1800);
    } catch (_e) {
      setCopyStatus("err");
    }
  };

  return (
    <div
      className="cs-detail-overlay"
      ref={overlayRef}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
      data-testid="cs-detail-overlay"
    >
      <div className="cs-detail-panel" role="dialog" aria-label={`${cs.name}'s story`}>
        <div className="cs-detail-head">
          <button
            className="cs-detail-back"
            onClick={onClose}
            aria-label="Close"
            data-testid="cs-detail-close"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to all stories
          </button>
          <div className="cs-detail-share">
            <button
              className="cs-detail-share-btn"
              onClick={copyShare}
              data-testid="cs-detail-share"
            >
              {copyStatus === "ok" ? "Link copied!" : copyStatus === "err" ? "Copy failed" : "Copy share link"}
            </button>
          </div>
        </div>

        <div className="cs-detail-grid">
          <div className="cs-detail-left">
            <span className="cs-detail-tag">{cs.tag}</span>
            <h2 className="cs-detail-title" data-testid="cs-detail-title">
              {cs.name}'s story
            </h2>
            <div className="cs-detail-meta">
              <span><Briefcase className="w-4 h-4" />{cs.role}</span>
              <span><MapPin className="w-4 h-4" />United States</span>
              <span><Clock className="w-4 h-4" />{cs.duration}</span>
            </div>

            <div className="cs-detail-stats">
              <div className="cs-detail-stat">
                <span className="cs-detail-stat-num">{cs.interviews}</span>
                <span className="cs-detail-stat-lbl">Interview calls</span>
              </div>
              <div className="cs-detail-stat">
                <span className="cs-detail-stat-num">{cs.duration}</span>
                <span className="cs-detail-stat-lbl">To land</span>
              </div>
            </div>

            <div className="cs-detail-pain">
              <span className="cs-detail-pain-lbl">The struggle</span>
              <p>"{cs.painPoint}"</p>
            </div>

            <div className="cs-detail-result">
              <TrendingUp className="w-4 h-4" />
              <span>{cs.result}</span>
            </div>

            <div className="cs-detail-summary">
              <Target className="w-4 h-4" />
              <p>{cs.summary}</p>
            </div>

            {cs.chat && cs.chat.length > 0 && (
              <div className="cs-chat" data-testid="cs-chat">
                <div className="cs-chat-head">WhatsApp conversation</div>
                <div className="cs-chat-list">
                  {cs.chat.map((m, i) => (
                    <div
                      key={i}
                      className={`cs-chat-bubble cs-chat-bubble-${m.from}`}
                    >
                      {m.text}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="cs-detail-right">
            {cs.screenshotUrl && (
              <div className="cs-detail-screenshot">
                <div className="cs-detail-screenshot-lbl">
                  <FileImage className="w-4 h-4" /> Real WhatsApp screenshot
                </div>
                <img
                  src={cs.screenshotUrl}
                  alt={`${cs.name}'s WhatsApp conversation`}
                  className="cs-detail-screenshot-img"
                />
              </div>
            )}
            <div className="cs-detail-cta">
              <h4>Want to be the next story?</h4>
              <p>Chat with us on WhatsApp — we'll show you the exact plan for your role.</p>
              <a
                href={WHATSAPP_CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => window.gtag_report_conversion && window.gtag_report_conversion()}
              >
                <Button className="cs-detail-cta-btn" data-testid="cs-detail-cta-btn">
                  Chat on WhatsApp <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Monthly Results section (reuse style) ---------- */
function ResultsViewer() {
  const [selectedMonth, setSelectedMonth] = useState(monthlyResults.length - 1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState("");
  const currentMonthData = monthlyResults[selectedMonth];
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const buildImageUrl = (pdfUrl, page) =>
    `${backendUrl}/api/pdf-page-image?url=${encodeURIComponent(pdfUrl)}&page=${page}&scale=1.2&quality=75`;

  useEffect(() => {
    const fetchInfo = async () => {
      if (!currentMonthData?.pdfUrl) return;
      try {
        const r = await fetch(
          `${backendUrl}/api/pdf-info?url=${encodeURIComponent(currentMonthData.pdfUrl)}`
        );
        const d = await r.json();
        setTotalPages(d.page_count);
      } catch (_e) {
        setTotalPages(currentMonthData.pages || 50);
      }
    };
    fetchInfo();
  }, [selectedMonth, currentMonthData?.pdfUrl, backendUrl, currentMonthData?.pages]);

  useEffect(() => {
    if (!currentMonthData?.pdfUrl) return;
    setIsLoading(true);
    setImageUrl(buildImageUrl(currentMonthData.pdfUrl, currentPage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedMonth, currentMonthData?.pdfUrl]);

  return (
    <section className="cs-results-section" id="results" data-testid="cs-results-section">
      <div className="cs-results-inner">
        <span className="cs-results-eyebrow">WALL OF PROOF</span>
        <h2 className="cs-results-title">Every month, hundreds of new offers</h2>
        <p className="cs-results-sub">
          Browse {totalPages || "…"} pages of fresh client wins from{" "}
          <strong>{currentMonthData.month} {currentMonthData.year}</strong>.
        </p>

        <div className="cs-results-months" data-testid="cs-results-months">
          {monthlyResults.map((m, i) => (
            <button
              key={m.id}
              className={`cs-results-month-btn ${selectedMonth === i ? "cs-results-month-active" : ""}`}
              onClick={() => {
                setSelectedMonth(i);
                setCurrentPage(1);
                setIsLoading(true);
              }}
              data-testid={`cs-results-month-${m.id}`}
            >
              <Calendar className="w-4 h-4" />
              <span>{m.month}</span>
              <span className="cs-results-month-year">{m.year}</span>
            </button>
          ))}
        </div>

        <div className="cs-results-viewer">
          <button
            className="cs-results-nav"
            onClick={() => currentPage > 1 && setCurrentPage((p) => p - 1)}
            disabled={currentPage <= 1 || isLoading}
            aria-label="Previous page"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="cs-results-stage">
            <div className="cs-results-stage-head">
              <span>
                {currentMonthData.month} {currentMonthData.year}
              </span>
              <span data-testid="cs-results-page-count">
                Page <strong>{currentPage}</strong> / {totalPages || "…"}
              </span>
            </div>
            <div className="cs-results-stage-img">
              {isLoading && (
                <div className="cs-results-loading">
                  <Loader2 className="w-8 h-8 animate-spin" />
                  <span>Loading page {currentPage}…</span>
                </div>
              )}
              {imageUrl && (
                <img
                  src={imageUrl}
                  data-testid="cs-results-page-img"
                  alt={`${currentMonthData.month} ${currentMonthData.year} — page ${currentPage}`}
                  onLoad={() => setIsLoading(false)}
                  onError={() => setIsLoading(false)}
                />
              )}
            </div>
          </div>
          <button
            className="cs-results-nav"
            onClick={() =>
              totalPages && currentPage < totalPages && setCurrentPage((p) => p + 1)
            }
            disabled={!totalPages || currentPage >= totalPages || isLoading}
            aria-label="Next page"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------- WhatsApp DM Marquee ---------- */
function DmMarquee() {
  const [expandedImage, setExpandedImage] = useState(null);

  const testimonialImages = Array.from({ length: 32 }, (_, i) => ({
    id: i + 2,
    image: `/testimonials_new/page_${i + 2}.png`,
  }));
  const firstRow = testimonialImages.slice(0, 16);
  const secondRow = testimonialImages.slice(16, 32);

  return (
    <section className="cs-dm-section" data-testid="cs-dm-marquee">
      <div className="cs-dm-inner">
        <span className="cs-dm-eyebrow">STRAIGHT FROM OUR DMs</span>
        <h2 className="cs-dm-title">
          More wins from <span className="cs-dm-accent">real conversations.</span>
        </h2>
        <p className="cs-dm-sub">
          Beyond the featured stories — dozens more WhatsApp messages from clients
          landing interviews and offers at top companies. Hover to pause, click to expand.
        </p>
      </div>

      <div className="testimonials-marquee-wrapper cs-dm-wrapper" data-testid="cs-dm-marquee-row-1">
        <div className="testimonials-marquee-track row-1">
          {[...firstRow, ...firstRow].map((item, index) => (
            <div
              key={`cs-r1-${item.id}-${index}`}
              className="testimonial-marquee-card"
              onClick={() => setExpandedImage(item)}
            >
              <img src={item.image} alt={`Client WhatsApp testimonial ${item.id}`} loading="lazy" />
            </div>
          ))}
        </div>
      </div>

      <div className="testimonials-marquee-wrapper cs-dm-wrapper" data-testid="cs-dm-marquee-row-2">
        <div className="testimonials-marquee-track row-2">
          {[...secondRow, ...secondRow].map((item, index) => (
            <div
              key={`cs-r2-${item.id}-${index}`}
              className="testimonial-marquee-card"
              onClick={() => setExpandedImage(item)}
            >
              <img src={item.image} alt={`Client WhatsApp testimonial ${item.id}`} loading="lazy" />
            </div>
          ))}
        </div>
      </div>

      {expandedImage && (
        <div
          className="testimonial-modal"
          onClick={() => setExpandedImage(null)}
          data-testid="cs-dm-expanded"
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setExpandedImage(null)}
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={expandedImage.image}
              alt="WhatsApp client success screenshot - BulkJobApply testimonial"
              className="modal-image"
            />
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------- Footer CTA ---------- */
function FooterCTA() {
  return (
    <section className="cs-footer-cta" data-testid="cs-footer-cta">
      <div className="cs-footer-cta-inner">
        <h2>Your story could be next.</h2>
        <p>Chat with us on WhatsApp — we'll show you exactly how we'll get you interviews.</p>
        <a
          href={WHATSAPP_CTA_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => window.gtag_report_conversion && window.gtag_report_conversion()}
        >
          <Button className="cs-footer-cta-btn" data-testid="cs-footer-cta-btn">
            Chat on WhatsApp <ArrowRight className="w-4 h-4" />
          </Button>
        </a>
      </div>
    </section>
  );
}

/* ---------- Page ---------- */
export default function CaseStudiesPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [tag, setTag] = useState(ALL);
  const [openCase, setOpenCase] = useState(null);

  /* Derive filter options from data */
  const { tags, stats } = useMemo(() => {
    const ts = Array.from(new Set(CASE_STUDIES.map((c) => c.tag).filter(Boolean))).sort();
    const totalInterviews = CASE_STUDIES.reduce((s, c) => s + (c.interviews || 0), 0);
    const weeks = CASE_STUDIES.map((c) => parseInt(c.duration, 10)).filter(Boolean);
    const avg = weeks.length ? Math.round(weeks.reduce((a, b) => a + b, 0) / weeks.length) : 0;
    return {
      tags: ts,
      stats: {
        total: CASE_STUDIES.length,
        totalInterviews,
        avgWeeks: `${avg} wks`,
      },
    };
  }, []);

  /* Open from URL slug */
  useEffect(() => {
    if (!slug) {
      setOpenCase(null);
      return;
    }
    const found = CASE_STUDIES.find((c) => slugify(c.name) === slug);
    if (found) {
      setOpenCase(found);
    } else {
      navigate("/casestudies", { replace: true });
    }
  }, [slug, navigate]);

  const handleOpen = (cs) => {
    setOpenCase(cs);
    navigate(`/casestudies/${slugify(cs.name)}`, { replace: false });
  };
  const handleClose = () => {
    setOpenCase(null);
    navigate("/casestudies", { replace: false });
  };

  /* Filter logic */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CASE_STUDIES.filter((c) => {
      if (tag !== ALL && c.tag !== tag) return false;
      if (!q) return true;
      const hay = [
        c.name, c.role, c.tag, c.painPoint, c.result, c.summary,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [query, tag]);

  return (
    <div className="cs-page" data-testid="cs-page">
      <CaseHeader />
      <CaseHero />
      <CaseFilters
        query={query}
        setQuery={setQuery}
        tag={tag}
        setTag={setTag}
        tags={tags}
      />

      <section className="cs-grid-section">
        <div className="cs-grid-inner">
          {filtered.length === 0 ? (
            <div className="cs-empty" data-testid="cs-empty">
              <h3>No stories match your filters.</h3>
              <p>Try clearing the search or selecting a different theme.</p>
              <Button
                onClick={() => {
                  setQuery("");
                  setTag(ALL);
                }}
                className="cs-empty-btn"
                data-testid="cs-empty-reset"
              >
                Reset filters
              </Button>
            </div>
          ) : (
            <div className="cs-grid" data-testid="cs-grid">
              {filtered.map((cs) => (
                <CaseCard key={cs.id} cs={cs} onOpen={handleOpen} />
              ))}
            </div>
          )}
        </div>
      </section>

      <ResultsViewer />
      <DmMarquee />
      <FooterCTA />

      {openCase && <CaseDetail cs={openCase} onClose={handleClose} />}
    </div>
  );
}
